import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  FormControlLabel,
  Checkbox,
  Grid,
  Divider,
  Tooltip,
  IconButton,
  Alert,
  Paper,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Slider
} from '@mui/material';
import { DEFAULT_WEB_ALERT_VOLUME } from '../../utils/webAlertSound';
import {
  Email as EmailIcon,
  Sms as SmsIcon,
  Notifications as PushIcon,
  Info as InfoIcon,
  PlayArrow as PlayIcon
} from '@mui/icons-material';

// Web notification sounds (files in public/sounds; 'default' is the legacy beep)
const WEB_SOUNDS = [
  { id: 'none', label: 'None (silent)' },
  { id: 'default', label: 'Default beep' },
  { id: 'chime', label: 'Chime' },
  { id: 'bell', label: 'Bell' },
  { id: 'alarm', label: 'Alarm' },
  { id: 'siren', label: 'Siren' },
  { id: 'pager', label: 'Pager' }
];

const webSoundUrl = (id) =>
  id === 'none' ? null : id === 'default' ? '/notification-sound.mp3' : `/sounds/alert_${id}.wav`;

const SimplifiedNotificationSettings = ({ 
  user, 
  onPreferencesChange, 
  isAdminEditing = false,
  disabled = false 
}) => {
  const [preferences, setPreferences] = useState(null);
  // Per-platform push delivery + web sound (stored inside notificationPreferences)
  const [platformSettings, setPlatformSettings] = useState({
    web: true,
    mobile: true,
    webAlertSound: 'default',
    webAlertVolume: DEFAULT_WEB_ALERT_VOLUME
  });

  // Role detection against the real user shape: primaryRole is OW/sOW/RM/SP/N-A
  // and admin rights live in secondaryRoles.admin; org membership is
  // organizationLevel (1|2). Legacy 'admin'/'owner'/'Org 1' values kept as fallbacks.
  const isAdminUser =
    ['OW', 'sOW', 'OWp', 'RM', 'admin', 'owner'].includes(user?.primaryRole) ||
    user?.secondaryRoles?.admin === true ||
    user?.secondaryRoles?.owner === true;
  const isOrg1User = user?.organizationLevel === 1 || user?.organization === 'Org 1';
  // Org 1 owners and regional managers: the people told about new provider applications
  const isManagement = isOrg1User && ['OW', 'sOW', 'OWp', 'RM'].includes(user?.primaryRole);

  // Default preferences
  const defaultPreferences = {
    newApplication: { enabled: true, email: true, sms: false, push: true },
    newJob: { enabled: true, email: true, sms: true, push: true },
    goa: { enabled: true, email: true, sms: false, push: true },
    jobAccepted: { enabled: true, email: true, sms: false, push: true },
    jobRejected: { enabled: true, email: true, sms: false, push: true },
    enRoute: { enabled: true, email: false, sms: false, push: true },
    onSite: { enabled: true, email: false, sms: false, push: true },
    completed: { enabled: true, email: false, sms: false, push: true },
    canceled: { enabled: true, email: true, sms: false, push: true }
  };

  // Tooltip content for each event
  const eventTooltips = {
    newApplication: "Owners: every new provider application that lands in Submissions. Regional managers: only applications whose facility is in one of your regions.",
    newJob: "Drivers: Notified when jobs are dispatched directly to them. Others: Notified about jobs for their organization or all jobs in the system (Org 1).",
    goa: "Notified when a job status is changed to 'Gone On Arrival' (GOA).",
    jobAccepted: "Notified when a visible job has been accepted by a driver and/or when an ETA is provided. Not available for driver-only users.",
    jobRejected: "Notified when a driver or provider rejects a job offer, including who rejected and why.",
    enRoute: "Notified when a job visible to you is set to 'En Route' status.",
    onSite: "Notified when a job visible to you is set to 'On Site' status.",
    completed: "Notified when a job visible to you is marked as 'Completed'.",
    canceled: "Notified when a job visible to you is set to 'Canceled' or 'Unsuccessful' status."
  };

  // Read an event's settings: admin bucket first (org-scoped), then self, then defaults
  const readAdminOrSelfEvent = (complexPrefs, adminKey, selfKey, def) => {
    const adminBucket = isAdminUser
      ? (isOrg1User
          ? complexPrefs?.admin?.events?.[adminKey]?.all
          : complexPrefs?.admin?.events?.[adminKey]?.forVendor)
      : null;
    const selfEvent = selfKey ? complexPrefs?.self?.events?.[selfKey] : null;
    return {
      enabled: adminBucket?.enabled ?? selfEvent?.enabled ?? def.enabled,
      email: adminBucket?.channels?.email ?? selfEvent?.channels?.email ?? def.email,
      sms: adminBucket?.channels?.sms ?? selfEvent?.channels?.sms ?? def.sms,
      push: adminBucket?.channels?.push ?? selfEvent?.channels?.push ?? def.push
    };
  };

  // Map complex preferences to simplified format
  const mapComplexToSimplified = (complexPrefs) => {
    if (!complexPrefs) {
      return { ...defaultPreferences };
    }

    const isAdmin = isAdminUser;
    const isOrg1 = isOrg1User;

    // For admins, check both self and admin events
    // For non-admins, only check self events
    let newJobEnabled, newJobEmail, newJobSms, newJobPush;
    let goaEnabled, goaEmail, goaSms, goaPush;
    let jobAcceptedEnabled, jobAcceptedEmail, jobAcceptedSms, jobAcceptedPush;
    let canceledEnabled, canceledEmail, canceledSms, canceledPush;
    
    if (isAdmin) {
      // Check admin events for new job notifications
      const adminNewJob = isOrg1 ? complexPrefs.admin?.events?.newJob?.all : complexPrefs.admin?.events?.newJob?.forVendor;
      newJobEnabled = adminNewJob?.enabled ?? complexPrefs.self?.events?.newJobAssigned?.enabled ?? defaultPreferences.newJob.enabled;
      newJobEmail = adminNewJob?.channels?.email ?? complexPrefs.self?.events?.newJobAssigned?.channels?.email ?? defaultPreferences.newJob.email;
      newJobSms = adminNewJob?.channels?.sms ?? complexPrefs.self?.events?.newJobAssigned?.channels?.sms ?? defaultPreferences.newJob.sms;
      newJobPush = adminNewJob?.channels?.push ?? complexPrefs.self?.events?.newJobAssigned?.channels?.push ?? defaultPreferences.newJob.push;

      // Check admin events for GOA notifications
      const adminGoa = isOrg1 ? complexPrefs.admin?.events?.jobMarkedAsGOA?.all : complexPrefs.admin?.events?.jobMarkedAsGOA?.forVendor;
      goaEnabled = adminGoa?.enabled ?? complexPrefs.self?.events?.jobMarkedAsGOA?.enabled ?? defaultPreferences.goa.enabled;
      goaEmail = adminGoa?.channels?.email ?? complexPrefs.self?.events?.jobMarkedAsGOA?.channels?.email ?? defaultPreferences.goa.email;
      goaSms = adminGoa?.channels?.sms ?? complexPrefs.self?.events?.jobMarkedAsGOA?.channels?.sms ?? defaultPreferences.goa.sms;
      goaPush = adminGoa?.channels?.push ?? complexPrefs.self?.events?.jobMarkedAsGOA?.channels?.push ?? defaultPreferences.goa.push;

      // Check admin events for ETA/job accepted notifications
      const adminEta = isOrg1 ? complexPrefs.admin?.events?.etaProvided?.all : complexPrefs.admin?.events?.etaProvided?.forVendor;
      jobAcceptedEnabled = adminEta?.enabled ?? complexPrefs.self?.events?.etaApproved?.enabled ?? defaultPreferences.jobAccepted.enabled;
      jobAcceptedEmail = adminEta?.channels?.email ?? complexPrefs.self?.events?.etaApproved?.channels?.email ?? defaultPreferences.jobAccepted.email;
      jobAcceptedSms = adminEta?.channels?.sms ?? complexPrefs.self?.events?.etaApproved?.channels?.sms ?? defaultPreferences.jobAccepted.sms;
      jobAcceptedPush = adminEta?.channels?.push ?? complexPrefs.self?.events?.etaApproved?.channels?.push ?? defaultPreferences.jobAccepted.push;

      // Check admin events for canceled/unsuccessful notifications
      const adminCanceled = isOrg1 ? complexPrefs.admin?.events?.jobCanceled?.all : complexPrefs.admin?.events?.jobCanceled?.forVendor;
      const adminUnsuccessful = isOrg1 ? complexPrefs.admin?.events?.jobUnsuccessful?.all : complexPrefs.admin?.events?.jobUnsuccessful?.forVendor;
      canceledEnabled = (adminCanceled?.enabled || adminUnsuccessful?.enabled) ?? 
                       (complexPrefs.self?.events?.jobCanceled?.enabled || complexPrefs.self?.events?.jobUnsuccessful?.enabled) ?? 
                       defaultPreferences.canceled.enabled;
      canceledEmail = (adminCanceled?.channels?.email || adminUnsuccessful?.channels?.email) ?? 
                     (complexPrefs.self?.events?.jobCanceled?.channels?.email || complexPrefs.self?.events?.jobUnsuccessful?.channels?.email) ?? 
                     defaultPreferences.canceled.email;
      canceledSms = (adminCanceled?.channels?.sms || adminUnsuccessful?.channels?.sms) ?? 
                   (complexPrefs.self?.events?.jobCanceled?.channels?.sms || complexPrefs.self?.events?.jobUnsuccessful?.channels?.sms) ?? 
                   defaultPreferences.canceled.sms;
      canceledPush = (adminCanceled?.channels?.push || adminUnsuccessful?.channels?.push) ?? 
                    (complexPrefs.self?.events?.jobCanceled?.channels?.push || complexPrefs.self?.events?.jobUnsuccessful?.channels?.push) ?? 
                    defaultPreferences.canceled.push;
    } else {
      // Non-admins only use self events
      newJobEnabled = complexPrefs.self?.events?.newJobAssigned?.enabled ?? defaultPreferences.newJob.enabled;
      newJobEmail = complexPrefs.self?.events?.newJobAssigned?.channels?.email ?? defaultPreferences.newJob.email;
      newJobSms = complexPrefs.self?.events?.newJobAssigned?.channels?.sms ?? defaultPreferences.newJob.sms;
      newJobPush = complexPrefs.self?.events?.newJobAssigned?.channels?.push ?? defaultPreferences.newJob.push;

      goaEnabled = complexPrefs.self?.events?.jobMarkedAsGOA?.enabled ?? defaultPreferences.goa.enabled;
      goaEmail = complexPrefs.self?.events?.jobMarkedAsGOA?.channels?.email ?? defaultPreferences.goa.email;
      goaSms = complexPrefs.self?.events?.jobMarkedAsGOA?.channels?.sms ?? defaultPreferences.goa.sms;
      goaPush = complexPrefs.self?.events?.jobMarkedAsGOA?.channels?.push ?? defaultPreferences.goa.push;

      jobAcceptedEnabled = complexPrefs.self?.events?.etaApproved?.enabled ?? defaultPreferences.jobAccepted.enabled;
      jobAcceptedEmail = complexPrefs.self?.events?.etaApproved?.channels?.email ?? defaultPreferences.jobAccepted.email;
      jobAcceptedSms = complexPrefs.self?.events?.etaApproved?.channels?.sms ?? defaultPreferences.jobAccepted.sms;
      jobAcceptedPush = complexPrefs.self?.events?.etaApproved?.channels?.push ?? defaultPreferences.jobAccepted.push;

      canceledEnabled = (complexPrefs.self?.events?.jobCanceled?.enabled || complexPrefs.self?.events?.jobUnsuccessful?.enabled) ?? 
                       defaultPreferences.canceled.enabled;
      canceledEmail = (complexPrefs.self?.events?.jobCanceled?.channels?.email || complexPrefs.self?.events?.jobUnsuccessful?.channels?.email) ?? 
                     defaultPreferences.canceled.email;
      canceledSms = (complexPrefs.self?.events?.jobCanceled?.channels?.sms || complexPrefs.self?.events?.jobUnsuccessful?.channels?.sms) ?? 
                   defaultPreferences.canceled.sms;
      canceledPush = (complexPrefs.self?.events?.jobCanceled?.channels?.push || complexPrefs.self?.events?.jobUnsuccessful?.channels?.push) ?? 
                    defaultPreferences.canceled.push;
    }

    // New provider application: a flat admin event (no org split), management only
    const savedApplication = complexPrefs.admin?.events?.applicationSubmitted;

    return {
      newApplication: {
        enabled: savedApplication?.enabled ?? defaultPreferences.newApplication.enabled,
        email: savedApplication?.channels?.email ?? defaultPreferences.newApplication.email,
        sms: savedApplication?.channels?.sms ?? defaultPreferences.newApplication.sms,
        push: savedApplication?.channels?.push ?? defaultPreferences.newApplication.push
      },
      newJob: {
        enabled: newJobEnabled,
        email: newJobEmail,
        sms: newJobSms,
        push: newJobPush
      },
      goa: {
        enabled: goaEnabled,
        email: goaEmail,
        sms: goaSms,
        push: goaPush
      },
      jobAccepted: {
        enabled: jobAcceptedEnabled,
        email: jobAcceptedEmail,
        sms: jobAcceptedSms,
        push: jobAcceptedPush
      },
      enRoute: readAdminOrSelfEvent(complexPrefs, 'jobEnRoute', 'jobEnRoute', defaultPreferences.enRoute),
      onSite: readAdminOrSelfEvent(complexPrefs, 'jobOnScene', 'jobOnScene', defaultPreferences.onSite),
      completed: readAdminOrSelfEvent(complexPrefs, 'jobCompleted', 'jobCompleted', defaultPreferences.completed),
      jobRejected: readAdminOrSelfEvent(complexPrefs, 'jobRejected', 'jobRejected', defaultPreferences.jobRejected),
      canceled: {
        enabled: canceledEnabled,
        email: canceledEmail,
        sms: canceledSms,
        push: canceledPush
      }
    };
  };

  // Map simplified preferences back to complex format
  const mapSimplifiedToComplex = (simplifiedPrefs, existingComplexPrefs) => {
    const complexPrefs = existingComplexPrefs || {
      channels: {
        email: { enabled: true, priority: 1 },
        sms: { enabled: true, priority: 2 },
        push: { enabled: true, priority: 3 }
      },
      self: {
        channels: {
          email: true,
          sms: true,
          push: true
        },
        events: {}
      },
      admin: {
        channels: {
          email: true,
          sms: true,
          push: true
        },
        events: {}
      },
      system: {
        channels: {
          email: true,
          sms: false,
          push: true
        },
        events: {}
      }
    };

    // IMPORTANT: Preserve global channel settings from existing preferences
    // These control whether the user receives notifications at all for each channel
    if (existingComplexPrefs?.channels) {
      complexPrefs.channels = { ...existingComplexPrefs.channels };
    }

    // Ensure self and events exist
    if (!complexPrefs.self) {
      complexPrefs.self = { channels: { email: true, sms: true, push: true }, events: {} };
    }
    if (!complexPrefs.self.events) {
      complexPrefs.self.events = {};
    }

    // Ensure admin and events exist
    if (!complexPrefs.admin) {
      complexPrefs.admin = { channels: { email: true, sms: true, push: true }, events: {} };
    }
    if (!complexPrefs.admin.events) {
      complexPrefs.admin.events = {};
    }

    const isAdmin = isAdminUser;
    const isOrg1 = isOrg1User;

    // Map simplified preferences to complex structure
    // Self events - for jobs assigned directly to the user
    complexPrefs.self.events.newJobAssigned = {
      enabled: simplifiedPrefs.newJob.enabled,
      channels: {
        email: simplifiedPrefs.newJob.email,
        sms: simplifiedPrefs.newJob.sms,
        push: simplifiedPrefs.newJob.push
      },
      urgency: 'high'
    };

    // Admin events - for admins to receive notifications about jobs in their organization
    if (isAdmin) {
      // Map newJob to admin notification events
      complexPrefs.admin.events.newJob = {
        all: {
          enabled: isOrg1 ? simplifiedPrefs.newJob.enabled : false,
          channels: {
            email: simplifiedPrefs.newJob.email,
            sms: simplifiedPrefs.newJob.sms,
            push: simplifiedPrefs.newJob.push
          }
        },
        forVendor: {
          enabled: !isOrg1 ? simplifiedPrefs.newJob.enabled : false,
          channels: {
            email: simplifiedPrefs.newJob.email,
            sms: simplifiedPrefs.newJob.sms,
            push: simplifiedPrefs.newJob.push
          }
        },
        unassigned: {
          enabled: simplifiedPrefs.newJob.enabled,
          channels: {
            email: simplifiedPrefs.newJob.email,
            sms: simplifiedPrefs.newJob.sms,
            push: simplifiedPrefs.newJob.push
          }
        }
      };

      // Map jobCanceled to admin notification events
      complexPrefs.admin.events.jobCanceled = {
        all: {
          enabled: isOrg1 ? simplifiedPrefs.canceled.enabled : false,
          channels: {
            email: simplifiedPrefs.canceled.email,
            sms: simplifiedPrefs.canceled.sms,
            push: simplifiedPrefs.canceled.push
          }
        },
        forVendor: {
          enabled: !isOrg1 ? simplifiedPrefs.canceled.enabled : false,
          channels: {
            email: simplifiedPrefs.canceled.email,
            sms: simplifiedPrefs.canceled.sms,
            push: simplifiedPrefs.canceled.push
          }
        }
      };

      // Map jobAccepted/ETA to admin notification events
      complexPrefs.admin.events.etaProvided = {
        all: {
          enabled: isOrg1 ? simplifiedPrefs.jobAccepted.enabled : false,
          channels: {
            email: simplifiedPrefs.jobAccepted.email,
            sms: simplifiedPrefs.jobAccepted.sms,
            push: simplifiedPrefs.jobAccepted.push
          }
        },
        forVendor: {
          enabled: !isOrg1 ? simplifiedPrefs.jobAccepted.enabled : false,
          channels: {
            email: simplifiedPrefs.jobAccepted.email,
            sms: simplifiedPrefs.jobAccepted.sms,
            push: simplifiedPrefs.jobAccepted.push
          }
        }
      };

      // Map GOA to admin notification events
      complexPrefs.admin.events.jobMarkedAsGOA = {
        all: {
          enabled: isOrg1 ? simplifiedPrefs.goa.enabled : false,
          channels: {
            email: simplifiedPrefs.goa.email,
            sms: simplifiedPrefs.goa.sms,
            push: simplifiedPrefs.goa.push
          }
        },
        forVendor: {
          enabled: !isOrg1 ? simplifiedPrefs.goa.enabled : false,
          channels: {
            email: simplifiedPrefs.goa.email,
            sms: simplifiedPrefs.goa.sms,
            push: simplifiedPrefs.goa.push
          }
        }
      };
    }

    complexPrefs.self.events.jobMarkedAsGOA = {
      enabled: simplifiedPrefs.goa.enabled,
      channels: {
        email: simplifiedPrefs.goa.email,
        sms: simplifiedPrefs.goa.sms,
        push: simplifiedPrefs.goa.push
      },
      urgency: 'medium'
    };

    complexPrefs.self.events.etaApproved = {
      enabled: simplifiedPrefs.jobAccepted.enabled,
      channels: {
        email: simplifiedPrefs.jobAccepted.email,
        sms: simplifiedPrefs.jobAccepted.sms,
        push: simplifiedPrefs.jobAccepted.push
      },
      urgency: 'medium'
    };

    complexPrefs.self.events.jobOnScene = {
      enabled: simplifiedPrefs.onSite.enabled,
      channels: {
        email: simplifiedPrefs.onSite.email,
        sms: simplifiedPrefs.onSite.sms,
        push: simplifiedPrefs.onSite.push
      },
      urgency: 'low'
    };

    complexPrefs.self.events.jobEnRoute = {
      enabled: simplifiedPrefs.enRoute.enabled,
      channels: {
        email: simplifiedPrefs.enRoute.email,
        sms: simplifiedPrefs.enRoute.sms,
        push: simplifiedPrefs.enRoute.push
      },
      urgency: 'low'
    };

    complexPrefs.self.events.jobAccepted = {
      enabled: simplifiedPrefs.jobAccepted.enabled,
      channels: {
        email: simplifiedPrefs.jobAccepted.email,
        sms: simplifiedPrefs.jobAccepted.sms,
        push: simplifiedPrefs.jobAccepted.push
      },
      urgency: 'medium'
    };

    complexPrefs.self.events.jobRejected = {
      enabled: simplifiedPrefs.jobRejected.enabled,
      channels: {
        email: simplifiedPrefs.jobRejected.email,
        sms: simplifiedPrefs.jobRejected.sms,
        push: simplifiedPrefs.jobRejected.push
      },
      urgency: 'high'
    };

    complexPrefs.self.events.jobCompleted = {
      enabled: simplifiedPrefs.completed.enabled,
      channels: {
        email: simplifiedPrefs.completed.email,
        sms: simplifiedPrefs.completed.sms,
        push: simplifiedPrefs.completed.push
      },
      urgency: 'low'
    };

    complexPrefs.self.events.jobCanceled = {
      enabled: simplifiedPrefs.canceled.enabled,
      channels: {
        email: simplifiedPrefs.canceled.email,
        sms: simplifiedPrefs.canceled.sms,
        push: simplifiedPrefs.canceled.push
      },
      urgency: 'medium'
    };

    complexPrefs.self.events.jobUnsuccessful = {
      enabled: simplifiedPrefs.canceled.enabled,
      channels: {
        email: simplifiedPrefs.canceled.email,
        sms: simplifiedPrefs.canceled.sms,
        push: simplifiedPrefs.canceled.push
      },
      urgency: 'medium'
    };

    // Continue mapping admin events for completed and unsuccessful jobs
    if (isAdmin) {
      // Org-scoped admin block: Org 1 admins use .all, Org 2 admins .forVendor
      const adminBlockFor = (prefs) => ({
        all: {
          enabled: isOrg1 ? prefs.enabled : false,
          channels: { email: prefs.email, sms: prefs.sms, push: prefs.push }
        },
        forVendor: {
          enabled: !isOrg1 ? prefs.enabled : false,
          channels: { email: prefs.email, sms: prefs.sms, push: prefs.push }
        }
      });

      complexPrefs.admin.events.jobEnRoute = adminBlockFor(simplifiedPrefs.enRoute);
      complexPrefs.admin.events.jobOnScene = adminBlockFor(simplifiedPrefs.onSite);
      complexPrefs.admin.events.jobAccepted = adminBlockFor(simplifiedPrefs.jobAccepted);
      complexPrefs.admin.events.jobRejected = adminBlockFor(simplifiedPrefs.jobRejected);

      complexPrefs.admin.events.jobCompleted = {
        all: {
          enabled: isOrg1 ? simplifiedPrefs.completed.enabled : false,
          channels: {
            email: simplifiedPrefs.completed.email,
            sms: simplifiedPrefs.completed.sms,
            push: simplifiedPrefs.completed.push
          }
        },
        forVendor: {
          enabled: !isOrg1 ? simplifiedPrefs.completed.enabled : false,
          channels: {
            email: simplifiedPrefs.completed.email,
            sms: simplifiedPrefs.completed.sms,
            push: simplifiedPrefs.completed.push
          }
        }
      };

      complexPrefs.admin.events.jobUnsuccessful = {
        all: {
          enabled: isOrg1 ? simplifiedPrefs.canceled.enabled : false,
          channels: {
            email: simplifiedPrefs.canceled.email,
            sms: simplifiedPrefs.canceled.sms,
            push: simplifiedPrefs.canceled.push
          }
        },
        forVendor: {
          enabled: !isOrg1 ? simplifiedPrefs.canceled.enabled : false,
          channels: {
            email: simplifiedPrefs.canceled.email,
            sms: simplifiedPrefs.canceled.sms,
            push: simplifiedPrefs.canceled.push
          }
        }
      };
    }

    // New provider application (Org 1 owners and RMs): stored flat under admin.events,
    // the backend reads it directly rather than through .all/.forVendor
    if (isManagement && simplifiedPrefs.newApplication) {
      complexPrefs.admin.events.applicationSubmitted = {
        enabled: simplifiedPrefs.newApplication.enabled,
        channels: {
          email: simplifiedPrefs.newApplication.email,
          sms: simplifiedPrefs.newApplication.sms,
          push: simplifiedPrefs.newApplication.push
        }
      };
    }

    return complexPrefs;
  };

  // Initialize preferences from user data
  useEffect(() => {
    if (user) {
      // Map existing complex preferences to simplified structure
      const simplifiedPrefs = mapComplexToSimplified(user.notificationPreferences);
      setPreferences(simplifiedPrefs);

      const stored = user.notificationPreferences || {};
      setPlatformSettings({
        web: stored.pushPlatforms?.web !== false,
        mobile: stored.pushPlatforms?.mobile !== false,
        webAlertSound: stored.webAlertSound || 'default',
        webAlertVolume: Number.isFinite(Number(stored.webAlertVolume)) ? Number(stored.webAlertVolume) : DEFAULT_WEB_ALERT_VOLUME
      });
    }
  }, [user]);

  // Build the full complex structure (events + platform settings) and notify parent
  const emitChange = (nextPreferences, nextPlatform) => {
    const complexPrefs = mapSimplifiedToComplex(nextPreferences, user.notificationPreferences);
    complexPrefs.pushPlatforms = { web: nextPlatform.web, mobile: nextPlatform.mobile };
    complexPrefs.webAlertSound = nextPlatform.webAlertSound;
    complexPrefs.webAlertVolume = nextPlatform.webAlertVolume;
    if (onPreferencesChange) onPreferencesChange(complexPrefs);
  };

  const preview = (sound, volume) => {
    const url = webSoundUrl(sound);
    if (!url) return;
    const audio = new Audio(url);
    audio.volume = Math.min(100, Math.max(0, Number(volume))) / 100;
    audio.play().catch(() => {});
  };

  const handlePlatformChange = (key, value) => {
    const next = { ...platformSettings, [key]: value };
    setPlatformSettings(next);
    if (key === 'webAlertSound') preview(value, next.webAlertVolume); // instant preview
    if (preferences) emitChange(preferences, next);
  };

  // Handle preference changes
  const handlePreferenceChange = (eventKey, channel, value) => {
    if (!preferences) return;

    const newPreferences = { ...preferences };
    
    if (channel === 'enabled') {
      // Toggle the entire event
      newPreferences[eventKey] = {
        ...newPreferences[eventKey],
        enabled: value
      };
    } else {
      // Toggle a specific channel
      newPreferences[eventKey] = {
        ...newPreferences[eventKey],
        [channel]: value
      };
    }

    setPreferences(newPreferences);

    // Convert back to complex format (incl. platform settings) and notify parent
    emitChange(newPreferences, platformSettings);
  };

  // Render channel controls for each event
  const renderEventChannels = (eventKey) => {
    return (
      <Grid container spacing={1} sx={{ ml: 2, mt: 0.5 }}>
        <Grid item xs={4}>
          <FormControlLabel
            control={
              <Checkbox
                checked={preferences?.[eventKey]?.email || false}
                onChange={(e) => handlePreferenceChange(eventKey, 'email', e.target.checked)}
                disabled={disabled}
                size="small"
              />
            }
            label={<EmailIcon fontSize="small" />}
          />
        </Grid>
        <Grid item xs={4}>
          <FormControlLabel
            control={
              <Checkbox
                checked={preferences?.[eventKey]?.sms || false}
                onChange={(e) => handlePreferenceChange(eventKey, 'sms', e.target.checked)}
                disabled={disabled}
                size="small"
              />
            }
            label={<SmsIcon fontSize="small" />}
          />
        </Grid>
        <Grid item xs={4}>
          <FormControlLabel
            control={
              <Checkbox
                checked={preferences?.[eventKey]?.push || false}
                onChange={(e) => handlePreferenceChange(eventKey, 'push', e.target.checked)}
                disabled={disabled}
                size="small"
              />
            }
            label={<PushIcon fontSize="small" />}
          />
        </Grid>
      </Grid>
    );
  };

  if (!preferences) {
    return <Typography>Loading notification preferences...</Typography>;
  }

  const isDriver =
    user?.primaryRole === 'driver' ||
    (user?.secondaryRoles?.driver === true &&
      !user?.secondaryRoles?.admin &&
      !user?.secondaryRoles?.dispatcher &&
      !user?.secondaryRoles?.answeringService);

  return (
    <Paper elevation={1} sx={{ p: 2 }}>
      <Typography variant="h6" gutterBottom>
        Notification Preferences
        <Tooltip title="Configure how you receive notifications for job events">
          <IconButton size="small" sx={{ ml: 1 }}>
            <InfoIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Typography>

      {isAdminEditing && (
        <Alert severity="info" sx={{ mb: 2 }}>
          You are editing notification preferences for {user?.firstName} {user?.lastName}
        </Alert>
      )}

      <Divider sx={{ my: 2 }} />

      {/* Push delivery: which devices receive push + the web sound */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="subtitle2" gutterBottom>
          Push delivery
          <Tooltip title="Choose which of your devices receive push notifications, and the sound this browser plays for incoming calls. These are independent — e.g. push on your phone but not the browser.">
            <IconButton size="small">
              <InfoIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={platformSettings.web}
                onChange={(e) => handlePlatformChange('web', e.target.checked)}
                disabled={disabled}
                size="small"
              />
            }
            label="Web browser"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={platformSettings.mobile}
                onChange={(e) => handlePlatformChange('mobile', e.target.checked)}
                disabled={disabled}
                size="small"
              />
            }
            label="Mobile app"
          />
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Web alert sound</InputLabel>
            <Select
              label="Web alert sound"
              value={platformSettings.webAlertSound}
              onChange={(e) => handlePlatformChange('webAlertSound', e.target.value)}
              disabled={disabled}
            >
              {WEB_SOUNDS.map((sound) => (
                <MenuItem key={sound.id} value={sound.id}>{sound.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <Tooltip title="Preview sound">
            <span>
              <IconButton
                size="small"
                disabled={platformSettings.webAlertSound === 'none'}
                onClick={() => preview(platformSettings.webAlertSound, platformSettings.webAlertVolume)}
              >
                <PlayIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Box sx={{ minWidth: 180, px: 1 }}>
            <Typography variant="caption" color="text.secondary">Loudness: {platformSettings.webAlertVolume}%</Typography>
            <Slider
              size="small"
              min={0}
              max={100}
              step={5}
              value={platformSettings.webAlertVolume}
              disabled={disabled || platformSettings.webAlertSound === 'none'}
              onChange={(e, v) => setPlatformSettings((cur) => ({ ...cur, webAlertVolume: v }))}
              onChangeCommitted={(e, v) => { preview(platformSettings.webAlertSound, v); handlePlatformChange('webAlertVolume', v); }}
              aria-label="Web alert loudness"
            />
          </Box>
        </Box>
        <Typography variant="caption" color="text.secondary">
          The mobile app's alert sound and loudness are chosen in the app's own Settings screen.
        </Typography>
      </Box>

      <Divider sx={{ my: 2 }} />

      {/* New Job */}
      <Box sx={{ mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={preferences?.newJob?.enabled || false}
                onChange={(e) => handlePreferenceChange('newJob', 'enabled', e.target.checked)}
                disabled={disabled}
              />
            }
            label="New Job"
          />
          <Tooltip title={eventTooltips.newJob}>
            <IconButton size="small">
              <InfoIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
        {preferences?.newJob?.enabled && renderEventChannels('newJob')}
      </Box>

      {/* GOA */}
      <Box sx={{ mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={preferences?.goa?.enabled || false}
                onChange={(e) => handlePreferenceChange('goa', 'enabled', e.target.checked)}
                disabled={disabled}
              />
            }
            label="GOA (Gone On Arrival)"
          />
          <Tooltip title={eventTooltips.goa}>
            <IconButton size="small">
              <InfoIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
        {preferences?.goa?.enabled && renderEventChannels('goa')}
      </Box>

      {/* Job Accepted - Hidden for driver-only users */}
      {!isDriver && (
        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={preferences?.jobAccepted?.enabled || false}
                  onChange={(e) => handlePreferenceChange('jobAccepted', 'enabled', e.target.checked)}
                  disabled={disabled}
                />
              }
              label="Job Accepted"
            />
            <Tooltip title={eventTooltips.jobAccepted}>
              <IconButton size="small">
                <InfoIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
          {preferences?.jobAccepted?.enabled && renderEventChannels('jobAccepted')}
        </Box>
      )}

      {/* Job Rejected - Hidden for driver-only users */}
      {!isDriver && (
        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={preferences?.jobRejected?.enabled || false}
                  onChange={(e) => handlePreferenceChange('jobRejected', 'enabled', e.target.checked)}
                  disabled={disabled}
                />
              }
              label="Job Rejected"
            />
            <Tooltip title={eventTooltips.jobRejected}>
              <IconButton size="small">
                <InfoIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
          {preferences?.jobRejected?.enabled && renderEventChannels('jobRejected')}
        </Box>
      )}

      {/* En Route */}
      <Box sx={{ mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={preferences?.enRoute?.enabled || false}
                onChange={(e) => handlePreferenceChange('enRoute', 'enabled', e.target.checked)}
                disabled={disabled}
              />
            }
            label="En Route"
          />
          <Tooltip title={eventTooltips.enRoute}>
            <IconButton size="small">
              <InfoIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
        {preferences?.enRoute?.enabled && renderEventChannels('enRoute')}
      </Box>

      {/* On Site */}
      <Box sx={{ mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={preferences?.onSite?.enabled || false}
                onChange={(e) => handlePreferenceChange('onSite', 'enabled', e.target.checked)}
                disabled={disabled}
              />
            }
            label="On Site"
          />
          <Tooltip title={eventTooltips.onSite}>
            <IconButton size="small">
              <InfoIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
        {preferences?.onSite?.enabled && renderEventChannels('onSite')}
      </Box>

      {/* Completed */}
      <Box sx={{ mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={preferences?.completed?.enabled || false}
                onChange={(e) => handlePreferenceChange('completed', 'enabled', e.target.checked)}
                disabled={disabled}
              />
            }
            label="Job Completed"
          />
          <Tooltip title={eventTooltips.completed}>
            <IconButton size="small">
              <InfoIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
        {preferences?.completed?.enabled && renderEventChannels('completed')}
      </Box>

      {/* Canceled */}
      <Box sx={{ mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={preferences?.canceled?.enabled || false}
                onChange={(e) => handlePreferenceChange('canceled', 'enabled', e.target.checked)}
                disabled={disabled}
              />
            }
            label="Job Canceled"
          />
          <Tooltip title={eventTooltips.canceled}>
            <IconButton size="small">
              <InfoIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
        {preferences?.canceled?.enabled && renderEventChannels('canceled')}
      </Box>

      {/* New provider application - Org 1 owners and regional managers only */}
      {isManagement && (
        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={preferences?.newApplication?.enabled || false}
                  onChange={(e) => handlePreferenceChange('newApplication', 'enabled', e.target.checked)}
                  disabled={disabled}
                />
              }
              label="New Provider Application"
            />
            <Tooltip title={eventTooltips.newApplication}>
              <IconButton size="small">
                <InfoIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
          {preferences?.newApplication?.enabled && renderEventChannels('newApplication')}
        </Box>
      )}
    </Paper>
  );
};

export default SimplifiedNotificationSettings;
